import React, { useState } from 'react';
import { Box, Alert, Container, MenuItem } from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import WbTextField from '../../../components/common/WbTextField';
import WbButton from '../../../components/common/WbButton';
import WbLoader from '../../../components/common/WbLoader';
import KioskLayout from '../../../components/kiosk/KioskLayout';
import { useKiosk } from '../../../contexts/KioskContext';
import kioskApi from '../../../services/api/kiosk/kioskApi';
import paths from '../../../routes/paths';
import '../KioskStyles.scss';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
// For kiosk student flow, UX only collects Day + Month.
// Backend will be updated to ignore year; we send a fixed year.
const KIOSK_DUMMY_YEAR = 2000;

export default function StudentVerify() {
  const { schoolSlug } = useParams();
  const location = useLocation();
  const { school, loading: schoolLoading } = useKiosk();
  const navigate = useNavigate();
  const student = location.state?.student;

  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (!student) {
      navigate(paths.kioskStudentSearch.replace(':schoolSlug', schoolSlug));
    }
  }, [student, navigate, schoolSlug]);

  const handleVerify = async () => {
    if (!day || !month) {
      setError('Please enter both day and month');
      return;
    }

    const dayNum = parseInt(day, 10);
    if (isNaN(dayNum) || dayNum < 1 || dayNum > 31) {
      setError('Please enter a valid day (1-31)');
      return;
    }

    const monthIndex = MONTHS.indexOf(month);
    if (monthIndex === -1) {
      setError('Please select a valid month');
      return;
    }

    // Use a fixed year; backend verification will compare only month & day.
    const yearNum = KIOSK_DUMMY_YEAR;
    const monthNum = (monthIndex + 1).toString().padStart(2, '0');
    const dayStr = dayNum.toString().padStart(2, '0');
    const dateOfBirth = `${yearNum}-${monthNum}-${dayStr}`;

    setVerifying(true);
    setError(null);

    try {
      const result = await kioskApi.verifyStudent(student.id, dateOfBirth);
      if (result.isValid) {
        // Store verified student info
        localStorage.setItem('kiosk-verified-student', JSON.stringify({
          studentId: result.student.id,
          studentName: `${result.student.firstName} ${result.student.lastName}`,
        }));
        navigate(paths.kioskStudentSurveys.replace(':schoolSlug', schoolSlug));
      } else {
        setError('Date of birth does not match. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  if (schoolLoading || !student) {
    return (
      <KioskLayout>
        <WbLoader />
      </KioskLayout>
    );
  }

  return (
    <KioskLayout>
      <Container maxWidth="md">
        <div className="kiosk-title">
          Verify Date of Birth
        </div>
        <div className="kiosk-subtitle">
          Please enter your date of birth
        </div>

        <Box className="kiosk-date-inputs">
          <WbTextField
            label="Day"
            value={day}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 2);
              setDay(value);
              setError(null);
            }}
            className="kiosk-date-field"
            autoFocus
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 2 }}
          />
          <WbTextField
            select
            label="Month"
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              setError(null);
            }}
            className="kiosk-date-field "            
          >
            {MONTHS.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </WbTextField>
        </Box>

        {error && (
          <Alert severity="error">{error}</Alert>          
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
          <WbButton
            CustomButtonText="Back"
            onClick={() => navigate(paths.kioskStudentSearch.replace(':schoolSlug', schoolSlug))} 
            className="kiosk-button-large"
          />
          <WbButton
            CustomButtonText={verifying ? 'Verifying...' : 'Continue'}
            onClick={handleVerify}
            disabled={verifying || !day || !month}
            className="kiosk-button-large"
          />
        </Box>
      </Container>
    </KioskLayout>
  );
}
